import React from 'react';
import { 
  Flower, 
  Leaf, 
  Bird, 
  Sun, 
  Grid3X3, 
  Building2, 
  Construction,
  CheckCircle2,
  Circle,
  AlertCircle
} from 'lucide-react';

const MotifDisplay = ({ motifDetails, rawAnalysis }) => {
  // Enhanced parser to handle specific formats
  const getParsedMotifs = () => {
    let baseData = null;
    
    // Ensure motifDetails is an object and not a string (which would cause iteration issues)
    const isStructuredObject = motifDetails && typeof motifDetails === 'object' && !Array.isArray(motifDetails) && Object.keys(motifDetails).length > 0;

    if (isStructuredObject) {
      baseData = JSON.parse(JSON.stringify(motifDetails)); // Deep clone
    } else if (rawAnalysis) {
      const sectionDefinitions = [
        { key: 'floral', label: 'Floral', regex: /(?:#*\s*Floral(?:\s*Motifs)?):?\s*\n?([\s\S]*?)(?=\n\n|\n#|\n[A-Z]{3,}|$)/i },
        { key: 'botanical', label: 'Botanical', regex: /(?:#*\s*Botanical(?:\s*\/ nature)?(?:\s*Motifs)?):?\s*\n?([\s\S]*?)(?=\n\n|\n#|\n[A-Z]{3,}|$)/i },
        { key: 'fauna', label: 'Fauna', regex: /(?:#*\s*Fauna(?:\s*Motifs)?):?\s*\n?([\s\S]*?)(?=\n\n|\n#|\n[A-Z]{3,}|$)/i },
        { key: 'celestial', label: 'Celestial', regex: /(?:#*\s*Celestial(?:\s*\/ cosmic)?(?:\s*Motifs)?):?\s*\n?([\s\S]*?)(?=\n\n|\n#|\n[A-Z]{3,}|$)/i },
        { key: 'geometric', label: 'Geometric', regex: /(?:#*\s*Geometric(?:\s*Motifs)?):?\s*\n?([\s\S]*?)(?=\n\n|\n#|\n[A-Z]{3,}|$)/i },
        { key: 'heritage', label: 'Heritage', regex: /(?:#*\s*Heritage(?:\s*\/ temple \/ deity)?(?:\s*Motifs)?):?\s*\n?([\s\S]*?)(?=\n\n|\n#|\n[A-Z]{3,}|$)/i },
        { key: 'structural', label: 'Structural', regex: /(?:#*\s*Structural(?:\s*decorative elements)?):?\s*\n?([\s\S]*?)(?=\n\n|\n#|\n[A-Z]{3,}|$)/i },
      ];

      const parsed = {};
      sectionDefinitions.forEach(({ key, regex }) => {
        const match = rawAnalysis.match(regex);
        if (match) {
          const content = match[1].trim();
          const lowerContent = content.toLowerCase();
          
          let status = 'Absent';
          if (lowerContent.includes('present') && !lowerContent.includes('absent')) {
            status = 'Present';
          } else if (lowerContent.includes('partially') || lowerContent.includes('partial')) {
            status = 'Partial';
          } else if (lowerContent.includes('present')) {
             status = 'Present';
          }

          parsed[key] = {
            status,
            notes: content.replace(/Present|Absent|Partially visible|Partial/gi, '').replace(/^[:\s-]+|[:\s-]+$/g, '').trim(),
            rawContent: content
          };
        }
      });
      baseData = parsed;
    }

    if (!baseData) return null;

    // Post-process to extract tags from notes for all items
    Object.keys(baseData).forEach(key => {
      const item = baseData[key];
      if (!item.tags) item.tags = [];
      
      const textToParse = item.rawContent || item.notes || '';
      if (textToParse) {
        // Extract tags (words starting with * or items in parentheses or slash-separated or comma-separated)
        const tags = [];
        
        // Handle comma-separated lists like "Phool, Pushpa" or "Lotus, Kamal"
        const commaGroups = textToParse.match(/\(([^)]+)\)/);
        if (commaGroups) {
          commaGroups[1].split(/[,/]/).forEach(part => {
            const p = part.trim();
            if (p && !p.toLowerCase().includes('relevant terminology')) tags.push(p);
          });
        }

        const tagMatches = textToParse.match(/\* (.*?)(?=\*|$|\n|:)/g) || [];
        tagMatches.forEach(tm => {
          let t = tm.replace(/^\* /, '').replace(/:.*$/, '').trim();
          if (t.includes('/')) {
            t.split('/').forEach(part => {
              const p = part.trim();
              if (p && !p.toLowerCase().includes('relevant terminology')) tags.push(p);
            });
          } else if (t.includes(',')) {
             t.split(',').forEach(part => {
                const p = part.trim();
                if (p && !p.toLowerCase().includes('relevant terminology')) tags.push(p);
             });
          } else if (t && !t.toLowerCase().includes('relevant terminology')) {
            tags.push(t);
          }
        });

        // Try to find keywords in notes even if not bulleted
        if (tags.length === 0 && (item.status.includes('Present') || item.status.includes('Partial'))) {
          const keywords = textToParse.match(/\(([^)]+)\)/g);
          if (keywords) {
            keywords.forEach(kw => {
              kw.replace(/[()]/g, '').split(/[/,]/).forEach(part => {
                const p = part.trim();
                if (p && p.length < 25 && !/\s/.test(p) === false) tags.push(p); // Prefer words with spaces or reasonable length
                else if (p && p.length < 15) tags.push(p); // Short single words are okay
              });
            });
          }
        }

        // Split camelCase or concatenated strings (e.g. PhoolPushpa -> Phool, Pushpa)
        const processedTags = [];
        const BLACKLIST = [
          'visual placement', 'prominence', 'symbolic', 'cultural association', 
          'relevant terminology', 'synonyms', 'describe', 'present', 'absent',
          'partially visible', 'secondary', 'accent', 'dominant'
        ];

        tags.forEach(tag => {
            // Skip if it's just prompt instructions
            if (BLACKLIST.some(b => tag.toLowerCase().includes(b))) return;

            // Handle concatenated terms like "MarquiseOvalEye" or "PhoolPushpa"
            // We look for transitions between lowercase and uppercase
            let cleanedTag = tag.replace(/([a-z])([A-Z])/g, '$1, $2');
            
            // If it was split, add the parts separately
            if (cleanedTag.includes(', ')) {
              cleanedTag.split(', ').forEach(part => {
                const p = part.trim();
                if (p && p.length > 1) processedTags.push(p);
              });
            } else {
              processedTags.push(tag);
            }
        });
        
        // Final sanity check for tags
        item.tags = [...new Set([...item.tags, ...processedTags])].filter(t => {
            // Remove too-short or too-long/concatenated garbage
            if (t.length < 2) return false;
            if (t.length > 20 && !t.includes(' ')) return false;
            // Final check against blacklist for the individual tag
            if (BLACKLIST.some(b => t.toLowerCase() === b)) return false;
            return true;
        });
      }
      
      // Normalize status
      if (item.status === 'Partially visible') item.status = 'Partial';
    });

    return baseData;
  };

  const data = getParsedMotifs();

  if (!data) return null;

  const categories = [
    { key: 'floral', label: 'Floral', icon: Flower },
    { key: 'botanical', label: 'Botanical', icon: Leaf },
    { key: 'fauna', label: 'Fauna', icon: Bird },
    { key: 'celestial', label: 'Celestial', icon: Sun },
    { key: 'geometric', label: 'Geometric', icon: Grid3X3 },
    { key: 'heritage', label: 'Heritage', icon: Building2 },
    { key: 'structural', label: 'Structural', icon: Construction },
  ];

  const presentCount = Object.values(data).filter(item => item.status === 'Present' || item.status === 'Partial').length;

  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('present')) {
      return { color: '#2e7d32', bg: '#e8f5e9', icon: CheckCircle2 };
    } else if (s.includes('partial') || s.includes('visible')) {
      return { color: '#ed6c02', bg: '#fff3e0', icon: AlertCircle };
    } else {
      return { color: '#9e9e9e', bg: '#f5f5f5', icon: Circle };
    }
  };

  return (
    <div className="motif-analysis-container">
      <div className="motif-title-header">
        <div className="motif-title">Motif & Symbolism Explorer</div>
        {presentCount > 0 && (
          <div className="motif-count-badge">
            {presentCount} Key Influences Found
          </div>
        )}
      </div>
      <div className="motif-grid">
        {categories.filter(cat => data[cat.key] && data[cat.key].status !== 'Absent').map((cat) => {
          const item = data[cat.key];
          const config = getStatusConfig(item.status);
          const Icon = cat.icon;
          const StatusIcon = config.icon;

          return (
            <div key={cat.key} className={`motif-item ${item.status.toLowerCase()}`}>
              <div className="motif-header">
                <div className="motif-category-icon">
                  <Icon size={22} />
                </div>
                <span className="motif-label">{cat.label}</span>
                <div className="motif-status-badge" style={{ backgroundColor: config.bg, color: config.color }}>
                  <StatusIcon size={14} />
                  <span>{item.status}</span>
                </div>
              </div>
              
              {item.tags && item.tags.length > 0 ? (
                <div className="motif-tag-container">
                  {item.tags.map((tag, idx) => (
                    <span key={idx} className="motif-tag">{tag}</span>
                  ))}
                </div>
              ) : (
                item.notes && (
                  <div className="motif-notes">
                    {item.notes.length > 100 ? item.notes.substring(0, 100) + '...' : item.notes}
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MotifDisplay;
